import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiDownload, FiPrinter, FiRotateCcw, FiTrendingDown, FiSearch } from 'react-icons/fi';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import useCurrency from '../../hooks/useCurrency';
import { navigateToTransaction } from '../../utils/navigationHelper';
import { formatAmount } from '../../utils/documentNumbering';
import useReportMemory from '../../hooks/useReportMemory';
import { addPrintBrand } from '../../utils/printBranding';

const StaffLoanAdvance = () => {
  const { activeCompany, activeBranch } = useAuth();
  // Backward memory: restore filters + loaded rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('staff-loan-advance');
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();

  const [staff, setStaff] = useState(() => savedMem?.staff ?? []);
  const [advances, setAdvances] = useState(() => savedMem?.advances ?? []);
  const [loans, setLoans] = useState(() => savedMem?.loans ?? []);
  const [loading, setLoading] = useState(() => savedMem ? false : true);

  const [staffFilter, setStaffFilter] = useState(() => savedMem?.staffFilter ?? 'all');
  const [typeFilter, setTypeFilter] = useState(() => savedMem?.typeFilter ?? 'all');  // 'all' | 'loan' | 'advance'
  const [statusFilter, setStatusFilter] = useState(() => savedMem?.statusFilter ?? 'outstanding'); // 'outstanding' | 'closed' | 'all'
  const [searchQuery, setSearchQuery] = useState(() => savedMem?.searchQuery ?? '');

  // ─── Load data ───
  const loadData = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const [staffSnap, advSnap, lonSnap] = await Promise.all([
        getDocs(query(collection(db, 'companies', activeCompany.id, 'salaryStaff'), orderBy('staffCode'))),
        getDocs(query(collection(db, 'companies', activeCompany.id, 'transactions'), where('tranTypeCode', '==', 'ADV'))),
        getDocs(query(collection(db, 'companies', activeCompany.id, 'transactions'), where('tranTypeCode', '==', 'LON')))
      ]);
      setStaff(staffSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      let advTxs = advSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      let lonTxs = lonSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (activeBranch && activeBranch.id !== 'all') {
        advTxs = advTxs.filter(t => t.branchId === activeBranch.id);
        lonTxs = lonTxs.filter(t => t.branchId === activeBranch.id);
      }
      setAdvances(advTxs);
      setLoans(lonTxs);
    } catch (e) { console.error('Load error:', e); }
    finally { setLoading(false); }
  }, [activeCompany, activeBranch]);

  // On Back (restored), SKIP ONLY the first auto-load — restored rows already hold
  // the data (0 re-reads) — but still let a later branch change re-fetch.
  const skipInitialLoad = useRef(!!savedMem);
  useEffect(() => {
    if (skipInitialLoad.current) { skipInitialLoad.current = false; return; }
    loadData();
  }, [loadData]);

  // Remember this view (filters + loaded rows) so Back restores it with 0 re-reads.
  // Staff/Type/Status/Search are display-only over already-loaded data, so we keep
  // the snapshot in sync as the user tweaks them (in-memory, ~free).
  useEffect(() => {
    if (loading) return;
    saveMemory({ staff, advances, loans, staffFilter, typeFilter, statusFilter, searchQuery });
  }, [loading, staff, advances, loans, staffFilter, typeFilter, statusFilter, searchQuery, saveMemory]);

  const resetFilters = () => {
    setStaffFilter('all');
    setTypeFilter('all');
    setStatusFilter('outstanding');
    setSearchQuery('');
  };

  // ─── Group per staff ───
  const grouped = useMemo(() => {
    const map = {};
    staff.forEach(s => {
      map[s.id] = {
        staffId: s.id,
        staffCode: s.staffCode,
        staffName: s.fullName,
        designation: s.designation,
        department: s.department,
        isActive: s.isActive !== false,
        advances: [],
        loans: [],
        advanceOutstanding: 0,
        loanOutstanding: 0,
        advancePaid: 0,
        loanPaid: 0
      };
    });

    advances.forEach(a => {
      if (!map[a.partyId]) return;  // skip if staff doesn't exist (shouldn't happen)
      // Apply filters
      if (typeFilter === 'loan') return;
      const isOutstanding = (a.outstandingAmount ?? a.totalAmount ?? 0) > 0 && a.status !== 'Cancelled';
      if (statusFilter === 'outstanding' && !isOutstanding) return;
      if (statusFilter === 'closed' && a.status !== 'Recovered' && a.status !== 'Cancelled') return;
      map[a.partyId].advances.push(a);
      map[a.partyId].advanceOutstanding += Number(a.outstandingAmount ?? a.totalAmount) || 0;
      map[a.partyId].advancePaid += Number(a.recoveredAmount) || 0;
    });

    loans.forEach(l => {
      if (!map[l.partyId]) return;
      if (typeFilter === 'advance') return;
      const isOutstanding = (l.outstandingAmount ?? l.totalAmount ?? 0) > 0 && l.status !== 'Cancelled';
      if (statusFilter === 'outstanding' && !isOutstanding) return;
      if (statusFilter === 'closed' && l.status !== 'Closed' && l.status !== 'Cancelled') return;
      map[l.partyId].loans.push(l);
      map[l.partyId].loanOutstanding += Number(l.outstandingAmount ?? l.totalAmount) || 0;
      map[l.partyId].loanPaid += Number(l.recoveredAmount) || 0;
    });

    let rows = Object.values(map).filter(r => r.advances.length > 0 || r.loans.length > 0);

    if (staffFilter !== 'all') rows = rows.filter(r => r.staffId === staffFilter);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter(r =>
        (r.staffName || '').toLowerCase().includes(q) ||
        (r.staffCode || '').toLowerCase().includes(q));
    }

    return rows.sort((a, b) => (b.loanOutstanding + b.advanceOutstanding) - (a.loanOutstanding + a.advanceOutstanding));
  }, [staff, advances, loans, staffFilter, typeFilter, statusFilter, searchQuery]);

  // ─── KPIs ───
  const kpis = useMemo(() => {
    let totalLoan = 0, totalAdvance = 0, staffCount = grouped.length;
    grouped.forEach(g => {
      totalLoan += g.loanOutstanding;
      totalAdvance += g.advanceOutstanding;
    });
    return { totalLoan, totalAdvance, staffCount, total: totalLoan + totalAdvance };
  }, [grouped]);

  // ─── Print ───
  const printReport = () => {
    const company = activeCompany || {};
    const branch = activeBranch && activeBranch.id !== 'all' ? activeBranch : null;
    const accent = '#800020';
    const escape = (str) => String(str ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

    const typeLabel = typeFilter === 'all' ? 'Loans + Advances' : typeFilter === 'loan' ? 'Loans only' : 'Advances only';
    const statusLabel = statusFilter === 'outstanding' ? 'Outstanding only' : statusFilter === 'closed' ? 'Closed only' : 'All statuses';
    const filterParts = [
      `<strong>Showing:</strong> ${typeLabel}`,
      `<strong>Status:</strong> ${statusLabel}`,
      staffFilter !== 'all' ? `<strong>Staff:</strong> ${escape(staff.find(s => s.id === staffFilter)?.fullName || '')}` : null,
      searchQuery ? `<strong>Search:</strong> ${escape(searchQuery)}` : null,
      branch ? `<strong>Branch:</strong> ${escape(branch.branchName)}` : null,
    ].filter(Boolean).join(' &nbsp;|&nbsp; ');

    const statusBadge = (status) => {
      const colors = {
        Closed: '#28A745', Recovered: '#28A745',
        Active: '#17A2B8', Issued: '#17A2B8',
        Cancelled: '#DC3545'
      };
      const c = colors[status] || '#999';
      return `<span style="background:${c}25;color:${c};padding:2px 6px;border-radius:3px;font-size:9px;font-weight:700">${escape(status)}</span>`;
    };

    const _dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__))) ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
    const _factor = Math.pow(10, _dp);
    const fmt = (n) => Number(Math.round((Number(n) || 0) * _factor) / _factor).toLocaleString('en-PK', { minimumFractionDigits: _dp, maximumFractionDigits: _dp });

    const staffSections = grouped.map(g => {
      const loanRows = g.loans.length === 0 ? '' : `
        <h4 class="sub">Loans (${g.loans.length})</h4>
        <table class="sub">
          <thead><tr>
            <th>Doc #</th><th>Date</th><th>Purpose</th>
            <th class="r">Principal</th><th class="c"># Inst.</th><th class="r">Monthly</th>
            <th class="r">Recovered</th><th class="r">Outstanding</th><th>Status</th>
          </tr></thead>
          <tbody>
            ${g.loans.map(l => `
              <tr>
                <td><strong>${escape(l.docNumber)}</strong></td>
                <td>${escape(l.docDate || '')}</td>
                <td style="color:#666;font-size:9px">${escape(l.purpose || '-')}</td>
                <td class="r">${fmt(l.totalAmount)}</td>
                <td class="c"><span class="inst">${l.paidInstallments || 0} / ${l.numberOfInstallments || 0}</span></td>
                <td class="r">${fmt(l.monthlyInstallment)}</td>
                <td class="r" style="color:#28A745">${fmt(l.recoveredAmount)}${(Number(l.voucherRecoveredAmount) || 0) > 0 ? `<div style="font-size:8px;color:#666;font-weight:400">incl. ${fmt(l.voucherRecoveredAmount)} by voucher</div>` : ''}</td>
                <td class="r" style="color:#b8860b;font-weight:700">${fmt(l.outstandingAmount ?? l.totalAmount)}</td>
                <td>${statusBadge(l.status)}</td>
              </tr>`).join('')}
          </tbody>
        </table>`;

      const advRows = g.advances.length === 0 ? '' : `
        <h4 class="sub">Advances (${g.advances.length})</h4>
        <table class="sub">
          <thead><tr>
            <th>Doc #</th><th>Date</th><th>Reason</th>
            <th class="r">Amount</th><th class="r">Recovered</th><th class="r">Outstanding</th><th>Status</th>
          </tr></thead>
          <tbody>
            ${g.advances.map(a => `
              <tr>
                <td><strong>${escape(a.docNumber)}</strong></td>
                <td>${escape(a.docDate || '')}</td>
                <td style="color:#666;font-size:9px">${escape(a.reason || '-')}</td>
                <td class="r">${fmt(a.totalAmount)}</td>
                <td class="r" style="color:#28A745">${fmt(a.recoveredAmount)}${(Number(a.voucherRecoveredAmount) || 0) > 0 ? `<div style="font-size:8px;color:#666;font-weight:400">incl. ${fmt(a.voucherRecoveredAmount)} by voucher</div>` : ''}</td>
                <td class="r" style="color:#b8860b;font-weight:700">${fmt(a.outstandingAmount ?? a.totalAmount)}</td>
                <td>${statusBadge(a.status)}</td>
              </tr>`).join('')}
          </tbody>
        </table>`;

      return `
        <div class="staff-card">
          <div class="staff-head">
            <div>
              <div class="staff-name">${escape(g.staffName)}${!g.isActive ? '<span class="inactive">Inactive</span>' : ''}</div>
              <div class="staff-meta">${escape(g.staffCode)}${g.designation ? ' · ' + escape(g.designation) : ''}${g.department ? ' · ' + escape(g.department) : ''}</div>
            </div>
            <div class="staff-totals">
              <div class="tot"><span>Loan</span><strong>${fmt(g.loanOutstanding)}</strong></div>
              <div class="tot"><span>Advance</span><strong>${fmt(g.advanceOutstanding)}</strong></div>
              <div class="tot total"><span>Total</span><strong>${fmt(g.loanOutstanding + g.advanceOutstanding)}</strong></div>
            </div>
          </div>
          ${loanRows}
          ${advRows}
        </div>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html>
<head>
<title>Staff Loan & Advance Report</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:'Segoe UI',sans-serif;color:#1A1A1A;padding:24px;line-height:1.4}
  @page{size:A4 landscape;margin:12mm}
  @media print{body{padding:0}}

  .header{display:flex;justify-content:space-between;align-items:flex-start;
    border-bottom:2px solid ${accent};padding-bottom:12px;margin-bottom:18px}
  .company-name{font-size:18px;font-weight:700;color:${accent}}
  .branch-name{font-size:13px;color:#444;font-weight:500}
  .meta{font-size:11px;color:#666;margin-top:2px}
  .title-block{text-align:right}
  .doc-title{color:${accent};font-size:22px;font-weight:700;letter-spacing:1px}

  .filters{font-size:12px;color:#444;margin-bottom:14px;padding:8px 12px;
    background:#f7f7f7;border-left:3px solid ${accent};border-radius:3px}

  .kpi-row{display:flex;gap:8px;margin-bottom:18px}
  .kpi{flex:1;padding:8px 10px;border:1px solid #ddd;border-radius:4px;text-align:center;background:white}
  .kpi.highlight{background:${accent};color:white;border-color:${accent}}
  .kpi-label{font-size:9px;color:#666;text-transform:uppercase;letter-spacing:0.4px;font-weight:600}
  .kpi.highlight .kpi-label{color:rgba(255,255,255,0.85)}
  .kpi-value{font-size:14px;font-weight:700;font-family:monospace;margin-top:3px}

  .staff-card{border:1px solid #ddd;border-radius:4px;margin-bottom:14px;overflow:hidden;page-break-inside:avoid}
  .staff-head{padding:10px 14px;background:#f5f5f5;border-bottom:1px solid #ddd;
    display:flex;justify-content:space-between;align-items:center;gap:16px;flex-wrap:wrap}
  .staff-name{font-size:14px;font-weight:700;color:#1A1A1A}
  .inactive{display:inline-block;margin-left:8px;padding:1px 6px;background:#ddd;color:#666;
    font-size:9px;font-weight:600;border-radius:8px}
  .staff-meta{font-size:10px;color:#888;margin-top:2px}
  .staff-totals{display:flex;gap:16px}
  .tot{text-align:right}
  .tot span{font-size:9px;color:#888;text-transform:uppercase;letter-spacing:0.4px;display:block;font-weight:600}
  .tot strong{font-size:13px;font-family:monospace;color:#333}
  .tot.total strong{color:${accent}}

  h4.sub{padding:8px 14px 4px;font-size:10px;color:#666;text-transform:uppercase;letter-spacing:0.5px;font-weight:600}

  table.sub{width:100%;border-collapse:collapse;margin:0 14px 10px;width:calc(100% - 28px);font-size:10px}
  table.sub th{background:#fafafa;color:#666;padding:6px 8px;text-align:left;font-size:9px;
    font-weight:600;text-transform:uppercase;letter-spacing:0.3px;border-bottom:1px solid #ddd;white-space:nowrap}
  table.sub td{padding:5px 8px;border-bottom:1px solid #eee}
  table.sub tr:last-child td{border-bottom:none}
  .c{text-align:center}
  .r{text-align:right;font-family:monospace}
  .inst{display:inline-block;padding:1px 6px;background:#17A2B815;color:#117A8B;
    border-radius:8px;font-weight:700;font-family:monospace;font-size:9px}

  .empty{text-align:center;padding:60px;color:#999;font-style:italic}
  .footer{margin-top:24px;padding-top:8px;border-top:1px solid #ddd;
    font-size:10px;color:#888;text-align:center}
</style>
</head>
<body>
  <div class="header">
    <div>
      ${branch
        ? `<div class="company-name">${escape(branch.branchName || '')}</div>
           <div class="branch-name">${escape(company.companyName || 'Company')}</div>`
        : `<div class="company-name">${escape(company.companyName || 'Company')}</div>`}
      ${(company.address || company.city) ? `<div class="meta">${escape([company.address, company.city].filter(Boolean).join(', '))}</div>` : ''}
      ${(company.ntn || company.gstNo) ? `<div class="meta">${[
          company.ntn ? ((company.taxLabel || 'GST').toUpperCase() === 'VAT' ? 'CR' : 'NTN') + ': ' + escape(company.ntn) : '',
          company.gstNo ? (escape(company.taxLabel || 'GST') + ': ' + escape(company.gstNo)) : ''
        ].filter(Boolean).join(' &nbsp;|&nbsp; ')}</div>` : ''}
    </div>
    <div class="title-block">
      <div class="doc-title">STAFF LOAN &amp; ADVANCE</div>
      <div class="meta">As of: ${new Date().toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}</div>
    </div>
  </div>

  <div class="filters">${filterParts}</div>

  <div class="kpi-row">
    <div class="kpi"><div class="kpi-label">Staff Count</div><div class="kpi-value">${kpis.staffCount}</div></div>
    <div class="kpi"><div class="kpi-label">Loan Outstanding</div><div class="kpi-value">${fmt(kpis.totalLoan)}</div></div>
    <div class="kpi"><div class="kpi-label">Advance Outstanding</div><div class="kpi-value">${fmt(kpis.totalAdvance)}</div></div>
    <div class="kpi highlight"><div class="kpi-label">Total Receivable</div><div class="kpi-value">${fmt(kpis.total)}</div></div>
  </div>

  ${grouped.length === 0
    ? `<div class="empty">No staff have outstanding loans or advances matching the current filters.</div>`
    : staffSections}

  <div class="footer">
    Staff Loan &amp; Advance Report · ${grouped.length} staff record${grouped.length !== 1 ? 's' : ''} · Printed from ${escape(company.companyName || 'AMAL ERP')}
  </div>
</body>
</html>`;

    const w = window.open('', '_blank');
    if (!w) { alert('Please allow pop-ups to print this report.'); return; }
    w.document.write(html);
    addPrintBrand(w);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 100);
  };

  // ─── Excel ───
  const exportToExcel = () => {
    const data = [];
    grouped.forEach(g => {
      g.loans.forEach(l => data.push({
        'Type': 'Loan',
        'Staff Code': g.staffCode,
        'Staff Name': g.staffName,
        'Department': g.department,
        'Doc #': l.docNumber,
        'Date': l.docDate,
        'Purpose': l.purpose,
        'Principal': l.totalAmount || 0,
        'Installments': `${l.paidInstallments || 0} / ${l.numberOfInstallments || 0}`,
        'Monthly': l.monthlyInstallment || 0,
        'Recovered': l.recoveredAmount || 0,
        'Recovered by Voucher': l.voucherRecoveredAmount || 0,
        'Outstanding': l.outstandingAmount ?? l.totalAmount ?? 0,
        'Status': l.status
      }));
      g.advances.forEach(a => data.push({
        'Type': 'Advance',
        'Staff Code': g.staffCode,
        'Staff Name': g.staffName,
        'Department': g.department,
        'Doc #': a.docNumber,
        'Date': a.docDate,
        'Purpose': a.reason,
        'Principal': a.totalAmount || 0,
        'Installments': '',
        'Monthly': '',
        'Recovered': a.recoveredAmount || 0,
        'Recovered by Voucher': a.voucherRecoveredAmount || 0,
        'Outstanding': a.outstandingAmount ?? a.totalAmount ?? 0,
        'Status': a.status
      }));
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Staff Loans & Advances');
    XLSX.writeFile(wb, `StaffLoanAdvance_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <PageLayout pageTitle="Staff Loan & Advance" breadcrumb="Reports / Salary / Staff Loan & Advance">
      <FiltersCard>
        <FilterGrid>
          <FilterItem>
            <FilterLabel>Staff</FilterLabel>
            <FilterSelect value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)}>
              <option value="all">All Staff</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.staffCode} — {s.fullName}</option>)}
            </FilterSelect>
          </FilterItem>
          <FilterItem>
            <FilterLabel>Type</FilterLabel>
            <FilterSelect value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="loan">Loan only</option>
              <option value="advance">Advance only</option>
            </FilterSelect>
          </FilterItem>
          <FilterItem>
            <FilterLabel>Status</FilterLabel>
            <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="outstanding">Outstanding only</option>
              <option value="closed">Closed only</option>
              <option value="all">All</option>
            </FilterSelect>
          </FilterItem>
          <FilterItem style={{ minWidth: 220 }}>
            <FilterLabel>Search</FilterLabel>
            <FilterInputWrap>
              <FiSearch />
              <input type="text" placeholder="Code or name..."
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </FilterInputWrap>
          </FilterItem>
          <FilterItem>
            <FilterLabel>&nbsp;</FilterLabel>
            <ResetBtn onClick={resetFilters}><FiRotateCcw /> Reset</ResetBtn>
          </FilterItem>
        </FilterGrid>
      </FiltersCard>

      <KPIGrid>
        <KPI><KL>Staff with Outstanding</KL><KV>{kpis.staffCount}</KV></KPI>
        <KPI><KL>Loan Outstanding</KL><KV>{formatCurrency(kpis.totalLoan)}</KV></KPI>
        <KPI><KL>Advance Outstanding</KL><KV>{formatCurrency(kpis.totalAdvance)}</KV></KPI>
        <KPI $highlight><KL>Total Receivable</KL><KV>{formatCurrency(kpis.total)}</KV></KPI>
      </KPIGrid>

      <ToolbarRow>
        <h3 style={{ color: colors.text.secondary, fontWeight: 600 }}>
          {grouped.length} staff record{grouped.length !== 1 ? 's' : ''}
        </h3>
        <div style={{ display: 'flex', gap: spacing.sm }}>
          <ExportBtn onClick={exportToExcel}><FiDownload /> Excel</ExportBtn>
          <ExportBtn onClick={printReport}><FiPrinter /> Print</ExportBtn>
        </div>
      </ToolbarRow>

      {loading ? <Loading>Loading...</Loading> : grouped.length === 0 ? (
        <Empty>
          <EmptyIcon><FiTrendingDown /></EmptyIcon>
          <p>No staff have outstanding loans or advances matching your filters.</p>
        </Empty>
      ) : (
        <div>
          {grouped.map(g => (
            <StaffCard key={g.staffId}>
              <StaffHeader>
                <div>
                  <StaffName>{g.staffName} <Sub>{g.staffCode}{g.designation ? ` · ${g.designation}` : ''}{g.department ? ` · ${g.department}` : ''}</Sub></StaffName>
                  {!g.isActive && <InactiveBadge>Inactive</InactiveBadge>}
                </div>
                <StaffTotals>
                  <Tot><span>Loan</span><strong>{formatCurrency(g.loanOutstanding)}</strong></Tot>
                  <Tot><span>Advance</span><strong>{formatCurrency(g.advanceOutstanding)}</strong></Tot>
                  <Tot $highlight><span>Total</span><strong>{formatCurrency(g.loanOutstanding + g.advanceOutstanding)}</strong></Tot>
                </StaffTotals>
              </StaffHeader>

              {g.loans.length > 0 && (
                <SubSection>
                  <SubTitle>Loans ({g.loans.length})</SubTitle>
                  <MiniTable>
                    <thead><tr>
                      <Th>Doc #</Th><Th>Date</Th><Th>Purpose</Th>
                      <Th style={{ textAlign: 'right' }}>Principal</Th>
                      <Th style={{ textAlign: 'center' }}># Inst.</Th>
                      <Th style={{ textAlign: 'right' }}>Monthly</Th>
                      <Th style={{ textAlign: 'right' }}>Recovered</Th>
                      <Th style={{ textAlign: 'right' }}>Outstanding</Th>
                      <Th>Status</Th>
                    </tr></thead>
                    <tbody>
                      {g.loans.map(l => (
                        <Tr key={l.id}>
                          <Td><strong onClick={() => navigateToTransaction(navigate, 'LON', l.id)} title="Open loan" style={{ color: colors.primary.main, cursor: 'pointer' }}>{l.docNumber}</strong></Td>
                          <Td>{l.docDate}</Td>
                          <Td style={{ fontSize: 12, color: colors.text.secondary }}>{l.purpose || '-'}</Td>
                          <Td style={{ textAlign: 'right' }}>{formatCurrency(l.totalAmount || 0)}</Td>
                          <Td style={{ textAlign: 'center' }}><InstBadge>{l.paidInstallments || 0} / {l.numberOfInstallments || 0}</InstBadge></Td>
                          <Td style={{ textAlign: 'right' }}>{formatCurrency(l.monthlyInstallment || 0)}</Td>
                          <Td style={{ textAlign: 'right', color: colors.success.dark }}>
                            {formatCurrency(l.recoveredAmount || 0)}
                            {(l.voucherRecoveredAmount || 0) > 0 && (
                              <div style={{ fontSize: 10, color: colors.text.secondary }}>incl. {formatCurrency(l.voucherRecoveredAmount)} by voucher</div>
                            )}
                          </Td>
                          <Td style={{ textAlign: 'right', fontWeight: 700, color: colors.warning.dark }}>{formatCurrency(l.outstandingAmount ?? l.totalAmount ?? 0)}</Td>
                          <Td><StatusTag $status={l.status}>{l.status}</StatusTag></Td>
                        </Tr>
                      ))}
                    </tbody>
                  </MiniTable>
                </SubSection>
              )}

              {g.advances.length > 0 && (
                <SubSection>
                  <SubTitle>Advances ({g.advances.length})</SubTitle>
                  <MiniTable>
                    <thead><tr>
                      <Th>Doc #</Th><Th>Date</Th><Th>Reason</Th>
                      <Th style={{ textAlign: 'right' }}>Amount</Th>
                      <Th style={{ textAlign: 'right' }}>Recovered</Th>
                      <Th style={{ textAlign: 'right' }}>Outstanding</Th>
                      <Th>Status</Th>
                    </tr></thead>
                    <tbody>
                      {g.advances.map(a => (
                        <Tr key={a.id}>
                          <Td><strong onClick={() => navigateToTransaction(navigate, 'ADV', a.id)} title="Open advance" style={{ color: colors.primary.main, cursor: 'pointer' }}>{a.docNumber}</strong></Td>
                          <Td>{a.docDate}</Td>
                          <Td style={{ fontSize: 12, color: colors.text.secondary }}>{a.reason || '-'}</Td>
                          <Td style={{ textAlign: 'right' }}>{formatCurrency(a.totalAmount || 0)}</Td>
                          <Td style={{ textAlign: 'right', color: colors.success.dark }}>
                            {formatCurrency(a.recoveredAmount || 0)}
                            {(a.voucherRecoveredAmount || 0) > 0 && (
                              <div style={{ fontSize: 10, color: colors.text.secondary }}>incl. {formatCurrency(a.voucherRecoveredAmount)} by voucher</div>
                            )}
                          </Td>
                          <Td style={{ textAlign: 'right', fontWeight: 700, color: colors.warning.dark }}>{formatCurrency(a.outstandingAmount ?? a.totalAmount ?? 0)}</Td>
                          <Td><StatusTag $status={a.status}>{a.status}</StatusTag></Td>
                        </Tr>
                      ))}
                    </tbody>
                  </MiniTable>
                </SubSection>
              )}
            </StaffCard>
          ))}
        </div>
      )}
    </PageLayout>
  );
};

// ─── Styles (shared with SalaryRegister patterns) ───
const FiltersCard = styled.div`background: ${colors.background.default}; border-radius: ${borderRadius.lg}; padding: ${spacing.md}; box-shadow: ${shadows.sm}; margin-bottom: ${spacing.md};`;
const FilterGrid = styled.div`display: flex; flex-wrap: wrap; gap: ${spacing.md}; align-items: flex-end;`;
const FilterItem = styled.div`display: flex; flex-direction: column; gap: 4px; min-width: 140px; flex: 1;`;
const FilterLabel = styled.label`font-size: 11px; font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase; letter-spacing: 0.5px;`;
const FilterSelect = styled.select`padding: ${spacing.sm}; border: 1px solid ${colors.border.light}; border-radius: ${borderRadius.sm}; font-size: ${typography.fontSize.sm}; background: ${colors.background.default};`;
const FilterInputWrap = styled.div`
  display: flex; align-items: center; gap: ${spacing.sm};
  padding: 0 ${spacing.sm}; border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm}; color: ${colors.text.secondary};
  input { border: none; outline: none; background: transparent; flex: 1; padding: ${spacing.sm} 0; }
`;
const ResetBtn = styled.button`
  display: flex; align-items: center; gap: 4px;
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.neutral[100]}; border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm}; font-size: ${typography.fontSize.sm}; cursor: pointer;
`;
const KPIGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: ${spacing.md}; margin-bottom: ${spacing.md};`;
const KPI = styled.div`
  background: ${props => props.$highlight ? colors.primary.main : colors.background.default};
  color: ${props => props.$highlight ? 'white' : colors.text.primary};
  padding: ${spacing.md}; border-radius: ${borderRadius.lg}; box-shadow: ${shadows.sm};
  border-left: 3px solid ${props => props.$highlight ? colors.primary.dark : colors.border.light};
`;
const KL = styled.div`font-size: 11px; opacity: 0.85; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;`;
const KV = styled.div`font-size: 22px; font-weight: 700; font-family: monospace;`;
const ToolbarRow = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: ${spacing.md}; flex-wrap: wrap; gap: ${spacing.md};`;
const ExportBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.primary.main}; color: white; border: none;
  border-radius: ${borderRadius.sm}; cursor: pointer; font-weight: 600; font-size: ${typography.fontSize.sm};
  &:hover { background: ${colors.primary.dark}; }
`;
const Loading = styled.div`padding: ${spacing.xxl}; text-align: center; color: ${colors.text.secondary};`;
const Empty = styled.div`padding: ${spacing.xxl}; text-align: center; background: ${colors.background.default}; border-radius: ${borderRadius.lg}; display: flex; flex-direction: column; align-items: center; gap: ${spacing.md}; color: ${colors.text.secondary};`;
const EmptyIcon = styled.div`width: 80px; height: 80px; border-radius: 50%; background: ${colors.primary.main}15; color: ${colors.primary.main}; display: flex; align-items: center; justify-content: center; font-size: 36px;`;

const StaffCard = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.lg};
  box-shadow: ${shadows.sm};
  margin-bottom: ${spacing.md};
  overflow: hidden;
`;
const StaffHeader = styled.div`
  padding: ${spacing.md} ${spacing.lg};
  background: ${colors.neutral[50]};
  border-bottom: 1px solid ${colors.border.light};
  display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: ${spacing.md};
`;
const StaffName = styled.div`font-size: ${typography.fontSize.md}; font-weight: 700; color: ${colors.text.primary};`;
const Sub = styled.span`font-size: 11px; color: ${colors.text.tertiary}; font-weight: 400; margin-left: 8px;`;
const InactiveBadge = styled.span`
  display: inline-block; margin-left: 8px;
  padding: 2px 8px; background: ${colors.neutral[200]}; color: ${colors.text.secondary};
  border-radius: ${borderRadius.xl}; font-size: 10px; font-weight: 600;
`;
const StaffTotals = styled.div`display: flex; gap: ${spacing.lg}; align-items: center;`;
const Tot = styled.div`
  text-align: right;
  span { font-size: 11px; color: ${colors.text.secondary}; text-transform: uppercase; letter-spacing: 0.5px; display: block; }
  strong { font-size: ${typography.fontSize.md}; font-family: monospace; color: ${props => props.$highlight ? colors.primary.main : colors.text.primary}; }
`;
const SubSection = styled.div`padding: ${spacing.md} ${spacing.lg};`;
const SubTitle = styled.h4`font-size: ${typography.fontSize.sm}; color: ${colors.text.secondary}; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: ${spacing.sm};`;
const MiniTable = styled.table`width: 100%; border-collapse: collapse;`;
const Th = styled.th`
  padding: 6px 10px; text-align: left; background: ${colors.neutral[100]};
  font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px;
  color: ${colors.text.secondary};
  border-bottom: 1px solid ${colors.border.light}; white-space: nowrap;
`;
const Tr = styled.tr`&:hover { background: ${colors.neutral[50]}; }`;
const Td = styled.td`padding: 6px 10px; font-size: ${typography.fontSize.sm}; border-bottom: 1px solid ${colors.neutral[100]};`;
const InstBadge = styled.span`
  display: inline-block; padding: 2px 8px;
  background: ${colors.info.main}15; color: ${colors.info.dark};
  border-radius: ${borderRadius.xl}; font-size: 11px; font-weight: 700; font-family: monospace;
`;
const StatusTag = styled.span`
  display: inline-block; padding: 2px 8px;
  background: ${props => {
    if (props.$status === 'Closed' || props.$status === 'Recovered') return colors.success.main + '20';
    if (props.$status === 'Active' || props.$status === 'Issued') return colors.info.main + '20';
    if (props.$status === 'Cancelled') return colors.error.main + '20';
    return colors.neutral[200];
  }};
  color: ${props => {
    if (props.$status === 'Closed' || props.$status === 'Recovered') return colors.success.dark;
    if (props.$status === 'Active' || props.$status === 'Issued') return colors.info.dark;
    if (props.$status === 'Cancelled') return colors.error.dark;
    return colors.text.secondary;
  }};
  border-radius: ${borderRadius.sm}; font-size: 10px; font-weight: 700;
`;

export default StaffLoanAdvance;
